'use client';
import { Box } from '@mui/material';
import { FC, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation, useWindowDimensions } from '@/hook';
import Typography from '@/components/shared/typography/typography.component';
import SylndrButton from '@/components/shared/Button/button-styled.component';
import { EFinanceStateUpdateValue, InterestRate } from '@/types';
import { numberWithCommas, originalCarPrice, PMT, removeCommas, roundToNearest } from '@/utils';
// import BottomSheetMobileAvailableCars from '../../mobile-bottom-sheet';
import colors from '@/theme/colors.module.scss';
import { useRouter } from 'next/navigation';
import BottomSheetMobileAvailableCars from '../../mobile-bottom-sheet';
import { useAnalytics } from '@/integrations';

export const FinancingResult: FC<{
    interestRates: InterestRate[];
    min: number;
    max: number;
    tenorCarPrice: number;
    downPayment: number;
    tenorEMI: number;
    isModalOpen: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    type: EFinanceStateUpdateValue;
}> = ({
    interestRates,
    min,
    max,
    downPayment,
    tenorEMI,
    isModalOpen,
    setIsModalOpen,
    tenorCarPrice,
    type,
}) => {
    const { t, i18n } = useTranslation();
    const { width } = useWindowDimensions();
    const router = useRouter();
    const { trackEvent } = useAnalytics();
    const {
        field: { value: emi },
    } = useController({ name: 'emi' });
    const {
        field: { value: dpAmount },
    } = useController({ name: 'dpAmount' });
    const {
        field: { value: originalPrice },
    } = useController({ name: 'originalPrice' });

    const isDesktop = width > 850;

    const monthlyInstallment = useMemo(() => removeCommas(emi), [emi]);
    const downPaymentAmount = useMemo(() => removeCommas(dpAmount), [dpAmount]);
    const carPrice = useMemo(
        () => removeCommas(originalPrice),
        [originalPrice]
    );

    const {
        formState: { errors },
    } = useFormContext();

    const downPaymentValue = useMemo(
        () => Math.ceil((downPayment / 100) * carPrice),
        [downPayment, carPrice]
    );

    const loanAmount = useMemo(
        () => Math.ceil(carPrice - downPaymentValue),
        [downPayment, carPrice]
    );

    const interestRate = useMemo(() => {
        if (type === 'carPrice') {
            const lenderInterestRates = interestRates?.filter(interestRate => {
                if (
                    !interestRate.lender.nameEn ||
                    !interestRate.lender.maxTenor ||
                    !interestRate.active
                ) {
                    return false;
                }
                return true;
            });
            return (
                lenderInterestRates
                    .map(item => item.interestRate)
                    .reduce((prev, curr) => prev + curr, 0) /
                (lenderInterestRates.length * 100)
            );
        }
        const lenderInterestRates = interestRates?.filter(interestRate => {
            if (
                !interestRate.lender.nameEn ||
                !interestRate.lender.maxTenor ||
                !interestRate.active
            ) {
                return false;
            }
            return (
                interestRate.downpayment === downPayment &&
                tenorEMI <= interestRate.lender.maxTenor
            );
        });
        if (!lenderInterestRates.length) {
            return null;
        }
        // calculate interest rate based on minimum interest rate of all financiers
        // lenderInterestRates.sort((a, b) => a.interestRate - b.interestRate);
        // const lenderInterestRate: InterestRate | undefined = lenderInterestRates[0];
        // return lenderInterestRate.interestRate / 100;

        // calculate interest rate based on average interest rate of all financiers
        const lenderInterestRate: number =
            lenderInterestRates.reduce(
                (prev, curr) => prev + curr.interestRate,
                0
            ) / lenderInterestRates.length;
        return lenderInterestRate / 100;
    }, [interestRates, downPayment, tenorEMI, tenorCarPrice, type]);

    const loanAmountMax = useMemo(() => {
        if (!min || !max || !interestRate) return 0;
        const dp = Math.ceil((downPayment / 100) * max);
        return Math.ceil(max - dp);
    }, [downPayment, carPrice]);

    const loanAmountMin = useMemo(() => {
        if (!min || !max || !interestRate) return 0;
        const dp = Math.ceil((downPayment / 100) * min);
        return Math.ceil(min - dp);
    }, [downPayment, carPrice]);

    const monthlyInstallmentResult = useMemo(() => {
        if (!interestRate || type === 'carPrice') {
            return 0;
        }
        const carPaymentPerMonth = PMT(interestRate, tenorEMI, loanAmount);
        return carPaymentPerMonth;
    }, [interestRates, interestRate, tenorEMI, loanAmount, type]);

    const carPriceResult = useMemo(() => {
        if (
            !monthlyInstallment ||
            !interestRate ||
            type === 'monthlyInstallments'
        )
            return 0;
        return roundToNearest(
            originalCarPrice(
                monthlyInstallment,
                interestRate,
                tenorCarPrice,
                downPaymentAmount
            ),
            1000
        );
    }, [
        interestRates,
        type,
        downPaymentAmount,
        interestRate,
        monthlyInstallment,
        tenorCarPrice,
    ]);

    const isDisabled = useMemo(() => {
        if (!min || !max || !interestRate) return true;
        if (type === 'carPrice')
            return (
                errors?.emi ||
                errors?.dpAmount ||
                carPriceResult < min ||
                carPriceResult > max
            );
        else
            return (
                errors?.originalPrice ||
                monthlyInstallmentResult <
                    PMT(interestRate, tenorEMI, loanAmountMin) ||
                monthlyInstallmentResult >
                    PMT(interestRate, tenorEMI, loanAmountMax)
            );
    }, [
        carPriceResult,
        monthlyInstallmentResult,
        type,
        loanAmountMax,
        loanAmountMin,
        errors?.emi,
        errors?.dpAmount,
        errors?.originalPrice,
    ]);
    const constructEventObject = () => {
        let eventObject: {
            sourceScreen: string;
            price: number;
            emi: number;
            downPayment: number | string;
            tenor: number;
        } = {
            sourceScreen: '',
            price: 0,
            emi: 0,
            downPayment: 0,
            tenor: 0,
        };
        if (type === 'carPrice') {
            eventObject = {
                sourceScreen: 'Monthly Installment',
                price: carPriceResult,
                emi: monthlyInstallment,
                downPayment: downPaymentAmount,
                tenor: tenorCarPrice,
            };
        }
        if (type === 'monthlyInstallments') {
            eventObject = {
                sourceScreen: 'Car Price',
                price: carPrice,
                emi: monthlyInstallmentResult,
                downPayment: `${downPayment}%`,
                tenor: tenorEMI,
            };
        }
        return eventObject;
    };
    const buttonClicked = () => {
        const eventObject = constructEventObject();
        trackEvent('Action: See All Available Cars', eventObject);
    };

    return (
        <>
            <Box
                sx={{
                    border: isDesktop ? 1 : 0,
                    borderColor: '#eaecf0',
                    borderRadius: isDesktop ? '8px' : '0px',
                    paddingY: isDesktop ? '24px' : '0px',
                    paddingX: isDesktop ? '16px' : '0px',
                    width: '100%',
                    mb: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#fff',
                    alignItems: 'flex-start',
                    gap: isDesktop ? '8px' : '16px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isDesktop ? 'column' : 'row',
                        justifyContent: isDesktop
                            ? 'flex-start'
                            : 'space-between',
                        alignItems: isDesktop ? 'flex-start' : 'flex-end',
                        width: '100%',
                        gap: '8px',
                    }}
                >
                    <Typography
                        variant="subtitle2Medium"
                        style={{
                            lineHeight: '20px',
                            color: '#00341F',
                            textAlign: 'end',
                        }}
                    >
                        {type === 'carPrice'
                            ? t('FinanceCalculator.carPrice')
                            : t('FinanceCalculator.monthlyInstallment')}
                    </Typography>

                    {
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                gap: '4px',
                            }}
                        >
                            <Typography
                                style={{
                                    lineHeight: '20px',
                                    color: colors.primary700,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                                variant={'caption'}
                            >
                                {t('EGP')}
                            </Typography>
                            <Typography
                                style={{
                                    color: colors.primary700,
                                }}
                                variant={'h6Bold'}
                            >
                                {numberWithCommas(
                                    type === 'carPrice'
                                        ? carPriceResult
                                        : monthlyInstallmentResult
                                )}
                            </Typography>
                        </Box>
                    }
                </Box>
                <SylndrButton
                    onClick={() => {
                        isDesktop
                            ? router.replace(
                                  `/${i18n.language}/buy-used-cars?price=${
                                      type === 'carPrice'
                                          ? `${carPriceResult*4/5},${carPriceResult*6/5}`
                                          : `${carPrice*4/5},${carPrice*6/5}`
                                  }`
                              )
                            : setIsModalOpen(true);
                        buttonClicked();
                    }}
                    disabled={isDisabled as boolean}
                    style={{
                        width: '100%',
                        padding: '10px 18px 10px 18px',
                    }}
                    variant={'primary'}
                >
                    <Typography variant={'body1Bold'}>
                        {t('FinanceCalculator.seeAvailableCars')}
                    </Typography>
                </SylndrButton>
            </Box>
            <BottomSheetMobileAvailableCars
                open={isModalOpen}
                priceToSearch={type === 'carPrice' ? carPriceResult : carPrice}
                setOpen={e => setIsModalOpen(e)}
            />
        </>
    );
};
